import { IconButton, Menu, MenuItem, Stack, styled, useTheme } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { Flag } from '@shared/components';
import { LanguageCapitalLettersCode } from '@shared/types';
import { useState } from 'react';

export type LanguageSwitchLocale = 'no' | 'se';

interface Props {
  selectedLanguage: string;
  languages: LanguageSwitchLocale[];
  switchLanguage: (languageCode: LanguageSwitchLocale) => void;
  flagSize?: string;
}

export const LanguageSwitch = ({
  selectedLanguage,
  switchLanguage,
  languages,
  flagSize = '27px',
}: Props) => {
  const t = useAppTFunction();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const selectedLanguageAsType = selectedLanguage as LanguageSwitchLocale;
  const handleLanguageButtons = (newLanguage: LanguageSwitchLocale) => {
    switchLanguage(newLanguage);
    setAnchorEl(null);
  };

  const args = {
    '&:hover': {
      outline: `1px solid ${theme.vars.palette.primary[900]}`,
      borderRadius: '50%',
    },
  };

  return (
    <Stack>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={t('common.changeLanguage')}
      >
        <Flag
          country={selectedLanguage.toUpperCase() as LanguageCapitalLettersCode}
          size={flagSize}
        />
      </IconButton>
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem
          aria-label={t(`common.changeTo.${selectedLanguageAsType}`)}
          key={selectedLanguageAsType}
          value={selectedLanguageAsType}
          onClick={() => handleLanguageButtons(selectedLanguageAsType)}
          sx={{ padding: 0.3, pt: 0.1 }}
        >
          <Flag
            country={selectedLanguageAsType.toUpperCase() as LanguageCapitalLettersCode}
            size={flagSize}
            sx={args}
          />
        </MenuItem>
        {languages.map(
          (language) =>
            selectedLanguageAsType !== language && (
              <MenuItem
                aria-label={t(`common.changeTo.${language}`)}
                key={language}
                value={language}
                onClick={() => handleLanguageButtons(language)}
                sx={{ padding: 0.3 }}
              >
                <Flag
                  country={language.toUpperCase() as LanguageCapitalLettersCode}
                  size={flagSize}
                  sx={args}
                />
              </MenuItem>
            )
        )}
      </StyledMenu>
    </Stack>
  );
};

const StyledMenu = styled(Menu)(({ theme }) => ({
  p: 0,
  '& .MuiMenu-paper': {
    padding: 0,
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[7],
    border: `1px solid ${theme.vars.palette.grey[50]}`,
  },
  '& .MuiMenu-list': { padding: 0 },
}));

export default LanguageSwitch;
